import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import type { ReadonlySignal } from '@preact/signals-core';
import { html, nothing, svg, type SVGTemplateResult } from 'lit';
import { property } from 'lit/decorators.js';

import type { Row } from '../../../../core/view-manager/row.js';
import { buildDependencyGraph } from '../../core/dependency-graph.js';
import { readBarStateFromCells } from '../../core/position-pipeline.js';
import { TimeAxisMath } from '../../core/time-axis-math.js';
import type { GanttZoomLevel } from '../../define.js';
import type { BarState } from '../../extensions/types.js';
import type { GanttSingleView } from '../../gantt-view-manager.js';
import { BAR_HEIGHT, BAR_VERTICAL_PADDING } from '../bars/bar-css.js';
import { ROW_HEIGHT } from '../bars/bar-renderer.js';
import {
  arrowHeadPoints,
  buildDependencyPath,
  type LineEndpoints,
} from './dependency-line.js';
import {
  dependencyArrowStyle,
  dependencyLineStyle,
  dependencyOverlayStyle,
  pendingLineStyle,
} from './dependency-overlay-css.js';
import { LINK_HANDLE_OFFSET_PX } from './link-hotspot-css.js';

interface BarBox {
  readonly startX: number;
  readonly endX: number;
  readonly midY: number;
}

/**
 * SVG layer rendered on top of the gantt bar layer. Subscribes to the
 * same signals the bar renderer does (rows, zoom, dependenciesCol,
 * originMs) and redraws A→B arrows whenever either end moves.
 *
 * Pointer-events are disabled on the host so the bars underneath stay
 * interactive; individual lines re-enable stroke hit testing so users
 * can hover for the tooltip.
 */
export class GanttDependencyOverlay extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @property({ attribute: false })
  accessor view!: GanttSingleView;

  @property({ attribute: false })
  accessor zoom: GanttZoomLevel = 'week';

  @property({ attribute: false })
  accessor originMs!: number;

  @property({ attribute: false })
  accessor fromMs!: number;

  @property({ attribute: false })
  accessor rowIndexOffset = 0;

  @property({ attribute: false })
  accessor visibleRows: readonly Row[] | undefined = undefined;

  /**
   * Pending link-drag endpoints in **client space**. Transformed into
   * overlay-local space at render time via the element's bounding
   * client rect. Drawn with a dashed stroke above the committed
   * dependency arrows.
   */
  @property({ attribute: false })
  accessor pendingEndpoints: LineEndpoints | undefined = undefined;

  /**
   * Live-preview snapshot of bars currently being dragged. When a
   * pointer drag is active, the bar renderer writes proposed
   * {@link BarState} values into this signal keyed by rowId; the
   * overlay reads the override first (falling back to the committed
   * cell state) so dependency arrows track the cursor in real time
   * instead of snapping to the committed position on pointerup.
   *
   * Passed in as a signal — not a plain map — so `SignalWatcher`
   * subscribes to the same source as the bar renderer and both
   * re-render on every pointermove. The overlay never writes to it.
   */
  @property({ attribute: false })
  accessor barPreviews:
    | ReadonlySignal<ReadonlyMap<string, BarState>>
    | undefined = undefined;

  override connectedCallback(): void {
    super.connectedCallback();
    this.classList.add(dependencyOverlayStyle);
  }

  override render() {
    const depsCol = this.view.dependenciesCol$.value;
    if (!depsCol) return html``;

    const rows = this.visibleRows ?? this.view.getBoundedRowsWindow().rows;
    if (rows.length === 0 && !this.pendingEndpoints) return html``;

    const math = new TimeAxisMath(this.zoom, this.originMs);
    const fromOffset = math.dateToX(this.fromMs);
    const rowIndexByRowId = new Map<string, number>();
    const boxes = new Map<string, BarBox>();
    // Signal read — SignalWatcher subscribes here so every preview
    // map replacement during a drag re-runs render() and repaints
    // the arrows in lock-step with the bars.
    const previews = this.barPreviews?.value;

    rows.forEach((row, localIndex) => {
      const rowIndex = localIndex + this.rowIndexOffset;
      rowIndexByRowId.set(row.rowId, rowIndex);
      // Prefer a live drag preview over the committed cell state so
      // arrows glide with the bar instead of snapping on commit.
      const state =
        previews?.get(row.rowId) ?? readBarStateFromCells(this.view, row);
      if (!state) return;
      const startX = math.dateToX(state.startMs) - fromOffset;
      const endX = math.dateToX(state.endMs) - fromOffset;
      const midY =
        rowIndex * ROW_HEIGHT + BAR_VERTICAL_PADDING + BAR_HEIGHT / 2;
      boxes.set(row.rowId, { startX, endX, midY });
    });

    const graph = buildDependencyGraph(this.view, rows);
    const arrows: SVGTemplateResult[] = [];
    for (const [fromRowId, targets] of graph) {
      const fromBox = boxes.get(fromRowId);
      if (!fromBox) continue;
      for (const toRowId of targets) {
        const toBox = boxes.get(toRowId);
        if (!toBox) continue;
        arrows.push(
          this.renderArrow({
            // Offset by LINK_HANDLE_OFFSET_PX so the arrow starts at
            // the *center of the source hotspot* (outside the bar's
            // right edge) and ends at the *center of the target
            // arrival marker* (outside the bar's left edge) — the
            // same anchors those circles draw at.
            fromX: fromBox.endX + LINK_HANDLE_OFFSET_PX,
            fromY: fromBox.midY,
            toX: toBox.startX - LINK_HANDLE_OFFSET_PX,
            toY: toBox.midY,
          })
        );
      }
    }

    const pending = this.pendingEndpoints
      ? this.renderPending(this.toLocalSpace(this.pendingEndpoints))
      : nothing;

    return html`
      <svg
        class=${dependencyOverlayStyle}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        ${arrows} ${pending}
      </svg>
    `;
  }

  private renderArrow(ep: LineEndpoints): SVGTemplateResult {
    return svg`
      <path class=${dependencyLineStyle} d=${buildDependencyPath(ep)}></path>
      <polygon class=${dependencyArrowStyle} points=${arrowHeadPoints(ep)}></polygon>
    `;
  }

  private renderPending(ep: LineEndpoints): SVGTemplateResult {
    return svg`<path class=${pendingLineStyle} d=${buildDependencyPath(ep)}></path>`;
  }

  private toLocalSpace(ep: LineEndpoints): LineEndpoints {
    const rect = this.getBoundingClientRect();
    return {
      fromX: ep.fromX - rect.left,
      fromY: ep.fromY - rect.top,
      toX: ep.toX - rect.left,
      toY: ep.toY - rect.top,
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-gantt-dependency-overlay': GanttDependencyOverlay;
  }
}
